import React from 'react';
import { Box, Typography } from '@availity/element';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../../static/constants/theme';
import hospitalUserIcon from '../../../static/assets/icons/hospital-user.svg';
import clipboardListIcon from '../../../static/assets/icons/clipboard-list.svg';
import moneyBillIcon from '../../../static/assets/icons/money-bill.svg';
import stethoscopeIcon from '../../../static/assets/icons/stethoscope.svg';
import Button from '../../Utils/components/Button';
import Animation from '../../Utils/components/Animation';

interface StepItemProps {
  number: string;
  title: string;
  description: string;
}

interface ApiCardProps {
  badge: string;
  title: string;
  description: string;
  onClick?: () => void;
}

const BLUE_ICON_FILTER = 'brightness(0) saturate(100%) invert(71%) sepia(18%) saturate(629%) hue-rotate(182deg) brightness(101%) contrast(88%)';

const StepItem: React.FC<StepItemProps> = ({ number, title, description }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, md: 2 }, width: '100%', maxWidth: 600 }}>
    <Box
      sx={{
        width: 48,
        height: 48,
        flexShrink: 0,
        background: colors.primary,
        borderRadius: '50%',
        border: '1px solid #0F2A7C',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography
        sx={{
          color: colors.white,
          fontSize: 16.95,
          fontFamily: 'Lato',
          fontWeight: 700,
          lineHeight: 1.416,
        }}
      >
        {number}
      </Typography>
    </Box>
    <Box sx={{ textAlign: 'left' }}>
      <Typography sx={{ color: 'white', fontSize: { xs: 18, md: 20 }, fontFamily: 'Montserrat', fontWeight: 600, lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Typography sx={{ color: 'white', fontSize: { xs: 16, md: 18 }, fontFamily: 'Lato', lineHeight: 1.444 }}>
        {description}
      </Typography>
    </Box>
  </Box>
);

const iconMap = {
  'Coverages': hospitalUserIcon,
  'Claim Statuses': clipboardListIcon,
  'Payment Accuracy': moneyBillIcon,
  'Patient Cost Estimator': stethoscopeIcon,
};

const ApiCard: React.FC<ApiCardProps> = ({ badge, title, description, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      flex: 1,
      p: { xs: 2, md: 3 },
      background: colors.primary,
      borderRadius: 3,
      border: '1px solid #0F2A7C',
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: 3, md: 4.875 },
      position: 'relative',
      cursor: onClick ? 'pointer' : 'default',
    }}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            background: '#1552BC',
            borderRadius: 12.5,
            display: 'inline-flex',
            alignItems: 'center',
            width: 'fit-content',
          }}
        >
          <Typography
            sx={{
              color: 'white',
              fontSize: 12,
              fontFamily: 'Lato',
              fontWeight: 700,
            }}
          >
            {badge}
          </Typography>
        </Box>
        <Typography
          sx={{
            color: 'white',
            fontSize: 20,
            fontFamily: 'Montserrat',
            fontWeight: 600,
            lineHeight: '24px',
            letterSpacing: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>
      </Box>
      <Typography
        sx={{
          color: 'white',
          fontSize: { xs: 18, md: 18 },
          fontFamily: 'Lato',
          fontWeight: 400,
          lineHeight: 1.444,
        }}
      >
        {description}
      </Typography>
    </Box>
    {iconMap[title] && (
      <Box
        component="img"
        src={iconMap[title]}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: 24,
          height: 24,
          filter: BLUE_ICON_FILTER,
        }}
      />
    )}
  </Box>
);

const ApiGatewaySection = () => {
  const navigate = useNavigate();
  
  const handleViewDocs = () => {
    navigate('/docs/api-spec');
  };

  const handleTagNavigation = (tag: string) => {
    navigate(`/docs/api-spec#tag/${tag}`, { state: { scrollToTag: tag } });
  };

  return (
  <Box
    sx={{
      width: 1,
      py: { xs: 8, md: 16.5 },
      background: '#000C30',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: { xs: 2, md: 3 },
    }}
  >
    <Box
      sx={{
        width: { xs: 1, lg: 1200 },
        maxWidth: 1200,
        px: { xs: 2, lg: 0 },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            textAlign: 'center',
            color: '#91B0DA',
            fontSize: { xs: 18, md: 18 },
            fontFamily: 'Lato',
            fontWeight: 400,
            lineHeight: 1.444,
          }}
        >
          Gateway APIs
        </Typography>
        <Typography
          sx={{
            textAlign: 'center',
            color: 'white',
            fontSize: { xs: 36, md: 36 },
            fontFamily: 'Montserrat',
            fontWeight: 600,
            lineHeight: 1.333,
          }}
        >
          Your Gateway to Healthcare API Innovation
        </Typography>
      </Box>
      
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: { xs: 5, lg: 0 },
        }}
      >
        <Box
          sx={{
            width: { xs: 1, lg: 700 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: { xs: 'center', lg: 'left' },
            gap: 4.875,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography
              sx={{
                color: 'white',
                fontSize: { xs: 28, md: 28 },
                fontFamily: 'Montserrat',
                fontWeight: 600,
                lineHeight: 1.428,
              }}
            >
              Start building in minutes with our intuitive API platform.
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: 1, alignItems: 'center' }}>
            <Typography
              sx={{
                color: colors.text.light,
                fontSize: { xs: 18, md: 18 },
                fontFamily: 'Lato',
                fontWeight: 400,
                lineHeight: 1.416,
                alignSelf: { xs: 'center', lg: 'flex-start' }
              }}
            >
              Integrate and accelerate your workflows:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
              <StepItem number="1" title="Sign up and verify your account" description="Quick onboarding with secure authentication." />
              <StepItem number="2" title="Explore our API products" description="Browse our catalog of eligibility & benefits, claims, and prior authorization APIs." />
              <StepItem number="3" title="Test and deploy" description="Use our sandbox environment to validate before you go live." />
            </Box>
          </Box>

          <Box sx={{ alignSelf: { xs: 'center', lg: 'flex-start' } }}>
            <Button variant="navbar-signup"
              sx={{
                width: 'fit-content'
              }}
              onClick={handleViewDocs}
            >
              View API Specifications
            </Button>
          </Box>
        </Box>
        
        <Box sx={{ display: 'block', width: { xs: 1, lg: 'auto' } }}>
          <Animation
            src="/static/assets/illustrations/Animation _ API Gateway.html"
            sx={{ width: { xs: '100%', lg: 602 }, aspectRatio: '602 / 624' }}
          />
        </Box>
      </Box>
    </Box>
    
    <Box
      id="coverages-section"
      sx={{
        width: { xs: 1, lg: 1200 },
        maxWidth: 1200,
        px: { xs: 2, lg: 0 },
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        gap: 3,
      }}
    >
      <ApiCard
        badge="Pre-Service"
        title="Coverages"
        description="Verify eligibility & benefits in real-time, directly with payers, in a HIPAA-compliant manner."
        onClick={() => handleTagNavigation('coverages')}
      />
      <ApiCard
        badge="Post-Service"
        title="Claim Statuses"
        description="Real-time claim status (276/277) across payers ensures fewer calls and faster resolutions."
        onClick={() => handleTagNavigation('claim-statuses')}
      />
      <ApiCard
        badge="Pre-Service"
        title="Payment Accuracy"
        description="Reduce denials and rework by stopping payment errors upstream with real‑time validation - improving first‑pass accuracy."
        onClick={() => handleTagNavigation('payment-accuracy')}
      />
      <ApiCard
        badge="Pre-Service"
        title="Patient Cost Estimator"
        description="Estimate patient out‑of‑pocket costs upfront using real‑time payer data—clearer pricing and fewer billing surprises."
        onClick={() => handleTagNavigation('patient-cost-estimator')}
      />
    </Box>
  </Box>
  );
};

export default ApiGatewaySection;